import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { isStandaloneMode } from '@/lib/dataProvider'
import MainView from '../views/MainView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'main',
      component: MainView,
      meta: { requiresAuth: true }
    },
    {
      path: '/scene/:sceneId',
      name: 'scene',
      component: MainView,
      meta: { requiresAuth: true },
      props: true
    },
    {
      path: '/integrations/spotify/callback',
      name: 'spotify-callback',
      component: () => import('../components/integrations/SpotifyCallback.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/present/:displayId',
      name: 'present',
      component: () => import('../views/PresentView.vue'),
      meta: { requiresAuth: true },
      props: true
    },
    {
      path: '/auth',
      name: 'auth',
      component: () => import('../views/AuthView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('../views/AuthCallbackView.vue')
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/ResetPasswordView.vue')
    },
    {
      path: '/impressum',
      name: 'impressum',
      component: () => import('../views/legal/ImpressumView.vue')
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/legal/PrivacyView.vue')
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/legal/TermsView.vue')
    },
    {
      path: '/sheet',
      name: 'sheet',
      component: () => import('../views/SheetView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/sheet/new',
      name: 'sheet-new',
      component: () => import('../views/SheetView.vue'),
      meta: { requiresAuth: true },
      props: { mode: 'new' }
    },
    {
      path: '/sheet/:characterId',
      name: 'sheet-character',
      component: () => import('../views/SheetView.vue'),
      meta: { requiresAuth: true },
      props: true
    },
    {
      path: '/gm',
      name: 'gm-dashboard',
      component: () => import('../views/GMDashboardView.vue'),
      meta: { requiresAuth: true, requiresOnline: true }
    },
    {
      path: '/gm/room/:roomId',
      name: 'gm-room',
      component: () => import('../views/GMDashboardView.vue'),
      meta: { requiresAuth: true, requiresOnline: true },
      props: true
    },
    {
      path: '/join',
      name: 'join-room',
      component: () => import('../views/JoinRoomView.vue'),
      meta: { requiresAuth: true, requiresOnline: true }
    },
    {
      path: '/join/:roomCode',
      name: 'join-room-code',
      component: () => import('../views/JoinRoomView.vue'),
      meta: { requiresAuth: true, requiresOnline: true },
      props: true
    },
  ],
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Wait for auth to initialize if not done yet
  while (authStore.loading) {
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest)
  const requiresOnline = to.matched.some(record => record.meta.requiresOnline)

  // Block online-only routes in standalone mode
  if (requiresOnline && isStandaloneMode()) {
    console.warn('Route requires online mode, redirecting to sheet')
    next({ name: 'sheet' })
    return
  }

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to auth page if not authenticated
    next({ name: 'auth' })
  } else if (requiresGuest && authStore.isAuthenticated) {
    // Redirect to main if already authenticated
    next({ name: 'main' })
  } else {
    next()
  }
})

export default router
